import {
  type QuoteRequest,
  type CipherText,
  Product,
} from "@b2bportal/purchase-api";
import { scheduleQuote } from "@hopper-b2b/api";
import type { RawAxiosRequestConfig } from "axios";

import {
  CartQuoteErrorModal,
  type CartQuoteError,
  CartQuoteState,
} from "../../../../types";
import type { CartContext } from "../../context";
import {
  getCartQuoteCustomerEmail,
  getCartQuoteProductModifications,
  getCartQuoteProducts,
} from "../selectors";

/**
 * Builds per-request HTTP options for the quote schedule call from the
 * products being quoted (e.g. tenant-specific attribution headers).
 * Returning `undefined` sends the request unchanged.
 */
export type GetScheduleQuoteRequestOptions = (
  products: QuoteRequest["products"]
) => RawAxiosRequestConfig | undefined;

/**
 * Builds the CartQuote `schedulePriceQuoteService`. Curried so a tenant can
 * bind its own `getRequestOptions` once, where it constructs its Services map,
 * and still hand XState the plain `(context, event)` service it expects.
 *
 * Tenants that need no per-request options should use the ready-made
 * `schedulePriceQuoteService` below rather than calling this themselves.
 */
export const makeSchedulePriceQuoteService =
  (getRequestOptions?: GetScheduleQuoteRequestOptions) =>
  (context: CartContext, _event: unknown) =>
    new Promise((resolve, reject) => {
      const products = getCartQuoteProducts({ context });
      const productMods = getCartQuoteProductModifications({ context });
      const email = getCartQuoteCustomerEmail({ context });
      const cartQuoteError: Omit<CartQuoteError, "type"> = {
        failedAt: CartQuoteState.schedule,
      };

      if (!products.length && !productMods.length) {
        return reject({
          ...cartQuoteError,
          type: CartQuoteErrorModal.NoProducts,
        });
      }

      if (!email) {
        return reject({
          ...cartQuoteError,
          type: CartQuoteErrorModal.NoEmail,
        });
      }

      // Block flight products with an empty phone before they reach
      // /quote/schedule (see ATL-1428). Both Flight and MultiProviderFlight
      // carry phoneNumber in SchedulePriceQuoteRequest.
      const flightProductMissingPhone = products.some(
        (product) =>
          (product.type === Product.Flight ||
            product.type === Product.MultiProviderFlight) &&
          (!product.value || !product.value.phoneNumber?.trim())
      );

      if (flightProductMissingPhone) {
        return reject({
          ...cartQuoteError,
          type: CartQuoteErrorModal.NoPhone,
        });
      }

      const params: QuoteRequest = {
        emailAddress: email!,
        products: products,
        modify: productMods,
      };

      scheduleQuote(params, getRequestOptions?.(products))
        .then((result: CipherText) => {
          if (result?.value) {
            resolve(result);
          } else {
            reject({
              ...cartQuoteError,
              type: CartQuoteErrorModal.NoCipherText,
            });
          }
        })
        .catch((e) => {
          reject({
            ...cartQuoteError,
            type: CartQuoteErrorModal.NoCipherText,
          });
        });
    });

export const schedulePriceQuoteService = makeSchedulePriceQuoteService();
